/**
 * Color Schemes for the 3D Geometry Viewer
 *
 * Provides color mapping functions for different visualization modes.
 */

import type { ColorScheme, SurfaceGeometry, BoundaryCondition, SurfaceType } from './types';

/**
 * Pre-defined colors for surface types.
 */
export const SURFACE_TYPE_COLORS: Record<SurfaceType, string> = {
  wall: '#D2B48C', // tan
  floor: '#808080', // gray
  roof: '#8B4513', // brown
  ceiling: '#FFFFFF', // white
};

/**
 * Pre-defined colors for boundary conditions.
 */
export const BOUNDARY_CONDITION_COLORS: Record<BoundaryCondition, string> = {
  outdoors: '#4169E1', // royal blue
  ground: '#8B4513', // brown
  adiabatic: '#808080', // gray
  surface: '#228B22', // forest green
  zone: '#9370DB', // medium purple
  othersidecoefficients: '#FF6347', // tomato
  othersideconditionsmodel: '#FF8C00', // dark orange
  groundfcfactormethod: '#654321', // dark brown
  groundslabpreprocessoraverage: '#8B4513',
  groundslabpreprocessorcore: '#A0522D',
  groundslabpreprocessorperimeter: '#CD853F',
  groundbasementpreprocessoraveragewall: '#6B4423',
  groundbasementpreprocessoraveragefloor: '#5C4033',
  groundbasementpreprocessorupperwall: '#704214',
  groundbasementpreprocessorlowerwall: '#4A3728',
  foundation: '#3D2914',
  unknown: '#C0C0C0', // silver
};

/**
 * Pre-defined colors for fenestration types.
 */
export const FENESTRATION_COLORS = {
  window: '#87CEEB', // light sky blue
  door: '#8B4513', // brown
  glassdoor: '#ADD8E6', // light blue
};

/**
 * Generate a distinct color based on a hash of the string.
 */
function hashStringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Generate HSL color with good saturation and lightness
  const h = Math.abs(hash % 360);
  const s = 55 + (Math.abs(hash >> 8) % 20); // 55-75%
  const l = 45 + (Math.abs(hash >> 16) % 20); // 45-65%

  return `hsl(${String(h)}, ${String(s)}%, ${String(l)}%)`;
}

/**
 * Cache for zone colors to ensure consistency.
 */
const zoneColorCache = new Map<string, string>();

/**
 * Get a color for a zone, using cached value if available.
 */
function getZoneColor(zoneName: string): string {
  const cached = zoneColorCache.get(zoneName);
  if (cached) {
    return cached;
  }
  const color = hashStringToColor(zoneName);
  zoneColorCache.set(zoneName, color);
  return color;
}

/**
 * Cache for construction colors.
 */
const constructionColorCache = new Map<string, string>();

/**
 * Get a color for a construction, using cached value if available.
 */
function getConstructionColor(constructionName: string): string {
  const cached = constructionColorCache.get(constructionName);
  if (cached) {
    return cached;
  }
  const color = hashStringToColor(constructionName);
  constructionColorCache.set(constructionName, color);
  return color;
}

/**
 * Get the color for a surface based on the selected color scheme.
 */
export function getSurfaceColor(surface: SurfaceGeometry, colorScheme: ColorScheme): string {
  switch (colorScheme) {
    case 'zone':
      return getZoneColor(surface.zoneName);

    case 'surface-type':
      return SURFACE_TYPE_COLORS[surface.type];

    case 'boundary-condition':
      return BOUNDARY_CONDITION_COLORS[surface.boundaryCondition];

    case 'construction':
      return getConstructionColor(surface.construction);

    default:
      return '#808080';
  }
}

/**
 * Clear color caches (useful when switching IDF files).
 */
export function clearColorCaches(): void {
  zoneColorCache.clear();
  constructionColorCache.clear();
}

/**
 * Get all unique zones and their colors.
 */
export function getZoneColorMap(zoneNames: string[]): Map<string, string> {
  const colorMap = new Map<string, string>();
  for (const name of zoneNames) {
    colorMap.set(name, getZoneColor(name));
  }
  return colorMap;
}

/**
 * Get all unique constructions and their colors.
 */
export function getConstructionColorMap(constructionNames: string[]): Map<string, string> {
  const colorMap = new Map<string, string>();
  for (const name of constructionNames) {
    colorMap.set(name, getConstructionColor(name));
  }
  return colorMap;
}

/**
 * Selection highlight color.
 */
export const SELECTION_COLOR = '#FFFF00'; // yellow

/**
 * Hover highlight color.
 */
export const HOVER_COLOR = '#00FFFF'; // cyan

/**
 * Wireframe color.
 */
export const WIREFRAME_COLOR = '#000000'; // black

/**
 * Grid color.
 */
export const GRID_COLOR = '#CCCCCC'; // light gray

/**
 * Axes colors.
 */
export const AXES_COLORS = {
  x: '#FF0000', // red
  y: '#00FF00', // green
  z: '#0000FF', // blue
};
